import { getTextField } from '@lender/utils';
import { isString } from 'lodash';
import { array, boolean, number, object, string } from 'yup';

import { COLOR_PALETTE } from 'constants/ui';
import { jsonParse } from 'utils/json';
import { getExpressionField } from 'utils/validation';

import {
  CycleMode,
  FIXED_DAYS_MIN,
  REMINDERS_INVALID_FALLBACK_MESSAGE,
  RENEWAL_WINDOW_MAX,
  RENEWAL_WINDOW_MIN,
} from './sections/CertificationCycleSection.constants';
import { parseRemindersText } from './sections/CertificationCycleSection.utils';

export const SectionTab = {
  COMMON: 'common',
  SECURITY_GROUPS: 'securityGroups',
  LOAN_ROLES: 'loanRoles',
  PIPELINES: 'pipelines',
  STEP_FLOW: 'stepFlow',
  CERTIFICATION_CYCLE: 'certificationCycle',
  BRANDING: 'branding',
  FORM_BUNDLES: 'formBundles',
  DOMAINS: 'domains',
  OPTION_LISTS: 'optionLists',
  TEST_FLOW: 'testFlow',
  HISTORY: 'history',
};

export const SECTION_TABS = [
  { label: 'Common', value: SectionTab.COMMON },
  { label: 'Security Groups', value: SectionTab.SECURITY_GROUPS },
  { label: 'Loan Roles', value: SectionTab.LOAN_ROLES },
  { label: 'Pipelines', value: SectionTab.PIPELINES },
  { label: 'Step Flow', value: SectionTab.STEP_FLOW },
  { label: 'Certification Cycle', value: SectionTab.CERTIFICATION_CYCLE },
  { label: 'Branding', value: SectionTab.BRANDING },
  { label: 'Form Bundles', value: SectionTab.FORM_BUNDLES },
  { label: 'Domains', value: SectionTab.DOMAINS },
  { label: 'Option Lists', value: SectionTab.OPTION_LISTS },
  { label: 'Test Flow', value: SectionTab.TEST_FLOW },
  { label: 'History', value: SectionTab.HISTORY },
];

export const DEFAULT_SECTION = SectionTab.COMMON;

export const VALIDATION_FIELD_SECTION_MAP = {
  name: SectionTab.COMMON,
  cycleMode: SectionTab.CERTIFICATION_CYCLE,
  cycleFixedDays: SectionTab.CERTIFICATION_CYCLE,
  recertificationRenewalWindowDays: SectionTab.CERTIFICATION_CYCLE,
  remindersEnabled: SectionTab.CERTIFICATION_CYCLE,
  remindersDaysBeforeExpiry: SectionTab.CERTIFICATION_CYCLE,
  signupSteps: SectionTab.STEP_FLOW,
  certificationSteps: SectionTab.STEP_FLOW,
  recertificationSteps: SectionTab.STEP_FLOW,
};

export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const baseSchemaShape = {
  name: getTextField(),
  cycleMode: string()
    .oneOf(Object.values(CycleMode), 'Select a valid cycle mode')
    .required('Cycle mode is required'),
  cycleFixedDays: number()
    .typeError('Fixed days must be a number')
    .when('cycleMode', {
      is: CycleMode.FIXED_DAYS,
      then: fieldSchema =>
        fieldSchema
          .required('Fixed days is required')
          .integer('Fixed days must be a whole number')
          .min(FIXED_DAYS_MIN, `Fixed days must be at least ${FIXED_DAYS_MIN}`),
      otherwise: fieldSchema => fieldSchema.nullable().notRequired(),
    }),
  recertificationRenewalWindowDays: number()
    .typeError('Renewal window must be a number')
    .required('Renewal window is required')
    .integer('Renewal window must be a whole number')
    .min(RENEWAL_WINDOW_MIN, 'Renewal window cannot be negative')
    .max(RENEWAL_WINDOW_MAX, `Renewal window cannot exceed ${RENEWAL_WINDOW_MAX}`),
  remindersEnabled: boolean(),
  remindersDaysBeforeExpiry: string().when('remindersEnabled', {
    is: true,
    then: fieldSchema =>
      fieldSchema.test(
        'reminders-decreasing-positive-integers',
        REMINDERS_INVALID_FALLBACK_MESSAGE,
        value => parseRemindersText(value).isValid,
      ),
    otherwise: fieldSchema => fieldSchema.notRequired(),
  }),
};

export const schema = object().shape(baseSchemaShape);

const getStepShape = ({
  schemaParams,
  isNewExpressionsParsingLogicEnabled,
  isExpressionPreserved,
}) =>
  object().shape({
    conditionExpression: getExpressionField({
      schemaParams,
      isRequired: false,
      isOnlyBooleanAllowed: true,
      isNewExpressionsParsingLogicEnabled,
      isPreserved: isExpressionPreserved,
    }).nullable(),
  });

export const getDrawerSchema = ({
  schemaParams,
  isNewExpressionsParsingLogicEnabled,
  isExpressionPreserved,
}) =>
  object().shape({
    ...baseSchemaShape,
    signupSteps: array().of(
      getStepShape({ schemaParams, isNewExpressionsParsingLogicEnabled, isExpressionPreserved }),
    ),
    certificationSteps: array().of(
      getStepShape({ schemaParams, isNewExpressionsParsingLogicEnabled, isExpressionPreserved }),
    ),
    recertificationSteps: array()
      .of(
        getStepShape({ schemaParams, isNewExpressionsParsingLogicEnabled, isExpressionPreserved }),
      )
      .nullable(),
  });

export const ColumnPhaseType = {
  SYSTEM: 'System',
  SIGNUP: 'Signup',
  CERTIFICATION: 'Certification',
  EITHER: 'Either',
};

export const StepKey = {
  NMLS_LOOKUP: 'NmlsLookup',
  LANDING_PAGE: 'LandingPage',
  OTP_VERIFICATION: 'OtpVerification',
  CONFIRMATION_PAGE: 'ConfirmationPage',
  COMPANY_DETAILS: 'CompanyDetails',
  LICENSE_ORIGINATOR: 'LicenseOriginator',
  DELIVERY_CHANNEL: 'DeliveryChannel',
  AE_SELECTION: 'AeSelection',
  OWNER_INFORMATION: 'OwnerInformation',
  UNDERWRITER_MANAGER: 'UnderwriterManager',
  ADMINISTRATOR_SETUP: 'AdministratorSetup',
  AFFILIATES: 'Affiliates',
  FORM_BUNDLE_SIGNING: 'FormBundleSigning',
  REVIEW_SUBMIT: 'ReviewSubmit',
  LOAN_PRODUCTION: 'LoanProduction',
  INVESTORS: 'Investors',
  AGENCY: 'Agency',
  COMPENSATION_PLANS: 'CompensationPlans',
  WAREHOUSE_BANK: 'WarehouseBank',
  TPO_DELIVERY: 'TpoDelivery',
  CONTRACT_SERVICES: 'ContractServices',
  DOCUMENT_UPLOAD: 'DocumentUpload',
  CONFIRM_DETAILS: 'ConfirmDetails',
  QUESTIONNAIRE: 'Questionnaire',
  SECURITY_QUESTIONNAIRE: 'SecurityQuestionnaire',
  PRIVACY_POLICY: 'PrivacyPolicy',
  ATTESTATION: 'Attestation',
  RUN_AI_PROCESS: 'RunAiProcess',
  DECISIONING: 'Decisioning',
};

export const STEP_REGISTRY = {
  [StepKey.NMLS_LOOKUP]: {
    defaultTitle: 'NMLS Lookup',
    phase: ColumnPhaseType.SYSTEM,
    isReorderable: false,
    fixedPosition: 'first',
    description: 'Auto-populate company data from NMLS database.',
  },
  [StepKey.LANDING_PAGE]: {
    defaultTitle: 'Login / Identity Entry',
    phase: ColumnPhaseType.SYSTEM,
    isReorderable: false,
    description:
      'Email or phone entry that triggers the OTP code. Shown after pre-login wizard steps.',
  },
  [StepKey.OTP_VERIFICATION]: {
    defaultTitle: 'OTP Verification',
    phase: ColumnPhaseType.SYSTEM,
    isReorderable: false,
    description: 'One-time code entry that completes identity verification.',
  },

  [StepKey.COMPANY_DETAILS]: {
    defaultTitle: 'Company Details',
    phase: ColumnPhaseType.SIGNUP,
    isReorderable: true,
    description: 'Collect company name, address, EIN/Tax ID, entity type.',
  },
  [StepKey.LICENSE_ORIGINATOR]: {
    defaultTitle: 'License Originator',
    phase: ColumnPhaseType.SIGNUP,
    isReorderable: true,
    description: 'Collect license originator type classification.',
  },
  [StepKey.DELIVERY_CHANNEL]: {
    defaultTitle: 'Delivery Channel',
    phase: ColumnPhaseType.SIGNUP,
    isReorderable: true,
    description: 'Select delivery channel (wholesale, correspondent, etc.).',
  },
  [StepKey.AE_SELECTION]: {
    defaultTitle: 'AE Selection',
    phase: ColumnPhaseType.SIGNUP,
    isReorderable: true,
    description: 'Choose assigned Account Executive during signup.',
  },

  [StepKey.OWNER_INFORMATION]: {
    defaultTitle: 'Owner Information',
    phase: ColumnPhaseType.CERTIFICATION,
    isReorderable: true,
    allowMultiple: true,
    description: 'Collect ownership details and background information.',
  },
  [StepKey.UNDERWRITER_MANAGER]: {
    defaultTitle: 'Underwriter Manager',
    phase: ColumnPhaseType.CERTIFICATION,
    isReorderable: true,
    description: 'Capture the Underwriting Manager, who need not be an owner.',
  },
  [StepKey.ADMINISTRATOR_SETUP]: {
    defaultTitle: 'Administrator Setup',
    phase: ColumnPhaseType.CERTIFICATION,
    isReorderable: true,
    description: 'Designate a company administrator for the account.',
  },
  [StepKey.AFFILIATES]: {
    defaultTitle: 'Affiliates',
    phase: ColumnPhaseType.CERTIFICATION,
    isReorderable: true,
    description: 'Affiliated businesses, parent companies, subsidiaries.',
  },
  [StepKey.FORM_BUNDLE_SIGNING]: {
    defaultTitle: 'Form Bundle Signing',
    phase: ColumnPhaseType.CERTIFICATION,
    isReorderable: false,
    fixedPosition: 'secondToLast',
    description: 'Sign all required form bundles. Always second-to-last.',
  },
  [StepKey.REVIEW_SUBMIT]: {
    defaultTitle: 'Review & Submit',
    phase: ColumnPhaseType.CERTIFICATION,
    isReorderable: false,
    fixedPosition: 'last',
    description: 'Final review and submission. Always last.',
  },
  [StepKey.CONFIRMATION_PAGE]: {
    defaultTitle: 'Confirmation Page',
    phase: ColumnPhaseType.SYSTEM,
    isReorderable: false,
    description:
      'Post-submission success page. Either auto-redirects to AdminPanel handoff or shows the reference number and next-steps copy in-place.',
  },

  [StepKey.LOAN_PRODUCTION]: {
    defaultTitle: 'Loan Production',
    phase: ColumnPhaseType.EITHER,
    isReorderable: true,
    description: 'Historical and projected loan production volume.',
  },
  [StepKey.INVESTORS]: {
    defaultTitle: 'Investors',
    phase: ColumnPhaseType.EITHER,
    isReorderable: true,
    description: 'Current investor relationships and approved list.',
  },
  [StepKey.AGENCY]: {
    defaultTitle: 'Agency',
    phase: ColumnPhaseType.EITHER,
    isReorderable: true,
    description: 'Agency approval details (Fannie Mae, Freddie Mac, Ginnie Mae).',
  },
  [StepKey.COMPENSATION_PLANS]: {
    defaultTitle: 'Compensation Plans',
    phase: ColumnPhaseType.EITHER,
    isReorderable: true,
    description: 'Select compensation plans during signup.',
  },
  [StepKey.WAREHOUSE_BANK]: {
    defaultTitle: 'Warehouse Bank',
    phase: ColumnPhaseType.EITHER,
    isReorderable: true,
    description: 'Warehouse line of credit details.',
  },
  [StepKey.TPO_DELIVERY]: {
    defaultTitle: 'TPO Delivery',
    phase: ColumnPhaseType.EITHER,
    isReorderable: true,
    description: 'Loan delivery preferences and methods.',
  },
  [StepKey.CONTRACT_SERVICES]: {
    defaultTitle: 'Contract Services',
    phase: ColumnPhaseType.EITHER,
    isReorderable: true,
    description: 'Third-party contract services information.',
  },
  [StepKey.DOCUMENT_UPLOAD]: {
    defaultTitle: 'Document Upload',
    phase: ColumnPhaseType.EITHER,
    isReorderable: true,
    allowMultiple: true,
    description: 'Upload required documents.',
  },
  [StepKey.CONFIRM_DETAILS]: {
    defaultTitle: 'Confirm Details',
    phase: ColumnPhaseType.EITHER,
    isReorderable: true,
    allowMultiple: true,
    description:
      'Ask the applicant to confirm previously-collected company details with a Yes/No prompt.',
  },
  [StepKey.QUESTIONNAIRE]: {
    defaultTitle: 'Questionnaire',
    phase: ColumnPhaseType.EITHER,
    isReorderable: true,
    allowMultiple: true,
    description: 'Configurable questionnaire (regulatory history, litigation, etc.).',
  },
  [StepKey.SECURITY_QUESTIONNAIRE]: {
    defaultTitle: 'Security Questionnaire',
    phase: ColumnPhaseType.EITHER,
    isReorderable: true,
    allowMultiple: true,
    description: 'Configurable security questionnaire (data handling, cybersecurity).',
  },
  [StepKey.PRIVACY_POLICY]: {
    defaultTitle: 'Privacy Policy',
    phase: ColumnPhaseType.EITHER,
    isReorderable: true,
    description: 'Review and accept privacy policy.',
  },
  [StepKey.ATTESTATION]: {
    defaultTitle: 'Attestation',
    phase: ColumnPhaseType.EITHER,
    isReorderable: true,
    description: 'Attest to accuracy of submitted information.',
  },
  [StepKey.RUN_AI_PROCESS]: {
    defaultTitle: 'Run AI Process',
    phase: ColumnPhaseType.EITHER,
    isReorderable: true,
    allowMultiple: true,
    description: 'Run a TPO-scoped AI process at this point in the flow.',
  },
  [StepKey.DECISIONING]: {
    defaultTitle: 'Decisioning',
    phase: ColumnPhaseType.EITHER,
    isReorderable: true,
    allowMultiple: true,
    description:
      'Evaluate boolean rules at this point in the flow. Each rule can create a task or block the applicant from advancing.',
  },
};

export const DEFAULT_SIGNUP_STEPS = [
  {
    key: StepKey.COMPANY_DETAILS,
    isEnabled: true,
    order: 0,
    isRequired: true,
    customTitle: null,
    customDescription: null,
  },
  {
    key: StepKey.LICENSE_ORIGINATOR,
    isEnabled: true,
    order: 1,
    isRequired: true,
    customTitle: null,
    customDescription: null,
  },
  {
    key: StepKey.DELIVERY_CHANNEL,
    isEnabled: true,
    order: 2,
    isRequired: true,
    customTitle: null,
    customDescription: null,
  },
  {
    key: StepKey.AE_SELECTION,
    isEnabled: false,
    order: 3,
    isRequired: false,
    customTitle: null,
    customDescription: null,
  },
];

export const DEFAULT_CERTIFICATION_STEPS = [
  {
    key: StepKey.OWNER_INFORMATION,
    isEnabled: true,
    order: 0,
    isRequired: true,
    customTitle: null,
    customDescription: null,
  },
  {
    key: StepKey.ADMINISTRATOR_SETUP,
    isEnabled: true,
    order: 1,
    isRequired: true,
    customTitle: null,
    customDescription: null,
  },
  {
    key: StepKey.PRIVACY_POLICY,
    isEnabled: true,
    order: 2,
    isRequired: true,
    customTitle: null,
    customDescription: null,
  },
  {
    key: StepKey.ATTESTATION,
    isEnabled: true,
    order: 3,
    isRequired: true,
    customTitle: null,
    customDescription: null,
  },
  {
    key: StepKey.FORM_BUNDLE_SIGNING,
    isEnabled: true,
    order: 98,
    isRequired: true,
    customTitle: null,
    customDescription: null,
  },
  {
    key: StepKey.REVIEW_SUBMIT,
    isEnabled: true,
    order: 99,
    isRequired: true,
    customTitle: null,
    customDescription: null,
  },
];

export const DEFAULT_BRANDING = {
  primaryColor: COLOR_PALETTE.BRAND[6],
  secondaryColor: COLOR_PALETTE.BRAND[7],
  accentColor: COLOR_PALETTE.ORANGE[6],
  logoUrl: '',
  faviconUrl: '',
  customCss: '',
};

export const parseSettings = config => {
  if (!config?.settings) {
    return {};
  }

  try {
    return isString(config.settings) ? jsonParse(config.settings) : config.settings;
  } catch {
    return {};
  }
};

// STP-429. Deliberately NOT RELOAD_AFTER_SAVE_FAILED_MESSAGE: that one opens with "Saved.", and
// on the conflict path nothing was saved — the whole point is that the write was refused.
export const RELOAD_AFTER_CONFLICT_FAILED_MESSAGE =
  'Your changes were not saved, and the current configuration could not be loaded. Reopen the configuration to see where it stands.';

export const RELOAD_AFTER_SAVE_FAILED_MESSAGE =
  'Saved. The configuration could not be reloaded, so reopen it before editing further.';

export const OWNER_DOCUMENT_BLOCKING_MESSAGE =
  'Owner Information: enable at least one acceptable document type for the required owner document, or turn off the per-owner document requirement.';

export const INVALID_FORM_BLOCKING_MESSAGE =
  'This configuration cannot be saved because some fields are invalid. Review the highlighted fields across the tabs and try again.';

export const buildNonExposablePipelinesMessage = names =>
  `Pipelines: no longer sub-company scoped — ${names.join(
    ', ',
  )}. Fix the filter in Loans › Pipelines or remove the exposure, then save again.`;

export const buildZeroChannelPipelinesMessage = names =>
  `Pipelines: no channels selected for — ${names.join(
    ', ',
  )}. Select at least one channel or switch the scope to all channels, then save again.`;

export const buildInvalidSectionsMessage = sections =>
  `This configuration cannot be saved — resolve the highlighted errors on: ${sections.join(', ')}.`;

// The array a channel gate compares against. Listed under both root casings
// because stored expressions use the camel-headed path while the schema's
// field options are Pascal-headed, and a label map keyed by path must match
// whichever spelling the surface hands us.
export const SELECTED_CHANNELS_PATHS = [
  'application.DeliveryChannel.SelectedChannels',
  'Application.DeliveryChannel.SelectedChannels',
];
